import type { SxProps } from '@mui/material';
import type { ImageGalleryFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import type { FlagProps } from '@packages/shared';
import { Box, Flag, Image, Stack } from '@packages/shared';
import type { FC } from 'react';

export type ImageGalleryFlagsProps = {
  flags: ImageGalleryFragmentFragment['additionalFlags'];
};

const stackStyle: SxProps = {
  touchAction: 'none',
  position: 'absolute',
  /* this has to be done with magic numbers because we cannot place the flag in the corresponding image container, 
  /* otherwise we will have a flag for every image in the carousel.
  /* 96px is the height of the thumbnails, 
  /* 0.5rem is the gap between the thumbnails and the main image
  /* 0.75 is the bottom spacing between the main image and the flag
  */
  bottom: 'calc(0.5rem + 0.75rem + 96px)',
  left: '0.75rem',
};

const imageContainerStyle: SxProps = {
  maxWidth: '5.625rem',
  width: '5.625rem',
  maxHeight: '1.5rem',
  height: '1.5rem',
  position: 'relative',
  '> img': {
    objectFit: 'contain',
    width: '100%',
  },
};

export const ImageGalleryFlags: FC<ImageGalleryFlagsProps> = ({ flags }) => (
  <Stack sx={stackStyle} gap={1} alignItems="flex-start">
    <div id="dy_info_bigpicture_dv" />
    {flags?.map(({ src, text, type }, index) =>
      /* custom attributes (images) are not renderer as flags, but as plain images instead */
      type !== 'CUSTOM' ? (
        <Flag
          key={text ?? index.toString()}
          type={type.toLowerCase() as FlagProps['type']}
          text={text ?? ''}
        />
      ) : (
        <Box key={text ?? index.toString()} sx={imageContainerStyle}>
          <Image src={src.concat('.png') ?? ''} alt={text ?? ''} fill />
        </Box>
      ),
    )}
  </Stack>
);
